import { createBrowserRouter } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { LandingPage } from '../pages/landing/LandingPage';
import { LpPage } from '../pages/landing/LpPage';
import { LP_SLUGS } from '../data/landingPages';
import { SUPPORTED_HOMEPAGE_LOCALES } from '../i18n/homepage';
import { blogRoutes } from './blog-routes';

const TablePlanFeaturePage = lazy(() => import('../pages/features/TablePlanFeaturePage').then(m => ({ default: m.TablePlanFeaturePage })));
const RsvpFeaturePage = lazy(() => import('../pages/features/RsvpFeaturePage').then(m => ({ default: m.RsvpFeaturePage })));
const PrivacyPage = lazy(() => import('../pages/legal/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('../pages/legal/TermsPage').then(m => ({ default: m.TermsPage })));
const PublicContactPage = lazy(() => import('../pages/contact/PublicContactPage').then(m => ({ default: m.PublicContactPage })));
const UnsubscribedPage = lazy(() => import('../pages/unsubscribe/UnsubscribedPage').then(m => ({ default: m.UnsubscribedPage })));

const wrap = (el: JSX.Element) => <Suspense fallback={null}>{el}</Suspense>;

// App routes (/login, /app/*, /admin/*) are served by the app bundle.
// If React Router SPA-navigates here, trigger a full page load so the
// browser requests index.html and gets the correct bundle.
function AppRedirect() {
  window.location.replace(window.location.href);
  return null;
}

// Consume SSR data injected by server.ts and convert it to React Router's
// hydrationData format so loaders don't re-run during hydration (fixes #418).
function buildHydrationData() {
  if (typeof window === 'undefined') return undefined;
  const ssrData = (window as Record<string, unknown>).__SSR_DATA__ as Record<string, unknown> | undefined;
  if (!ssrData) return undefined;
  delete (window as Record<string, unknown>).__SSR_DATA__;

  const pathname = window.location.pathname;
  let routeId: string;
  if (ssrData.post) {
    routeId = 'blog-post';
  } else if (pathname.startsWith('/blog/category/')) {
    routeId = 'blog-category';
  } else {
    routeId = 'blog-list';
  }
  return { loaderData: { [routeId]: ssrData } };
}

export const marketingRouter = createBrowserRouter([
  { path: '/', element: <LandingPage /> },
  ...SUPPORTED_HOMEPAGE_LOCALES.map(locale => ({
    path: `/${locale}`,
    element: <LandingPage locale={locale} />,
  })),
  ...LP_SLUGS.map(slug => ({ path: `/${slug}`, element: <LpPage /> })),
  { path: '/wedding-seating-planner', element: wrap(<TablePlanFeaturePage />) },
  { path: '/features/rsvp', element: wrap(<RsvpFeaturePage />) },
  ...blogRoutes,
  { path: '/privacy', element: wrap(<PrivacyPage />) },
  { path: '/terms', element: wrap(<TermsPage />) },
  { path: '/contact', element: wrap(<PublicContactPage />) },
  { path: '/unsubscribed', element: wrap(<UnsubscribedPage />) },
  // All other routes (/login, /app/*, /admin/*) are handled by the app bundle
  { path: '*', element: <AppRedirect /> },
], { hydrationData: buildHydrationData() });
